import { useState, useEffect, useCallback, Suspense, lazy } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';

import Header from './components/layout/Header';
import Footer from './components/layout/Footer';
import BackToTopButton from './components/common/BackToTopButton';
import GlobalSearch from './components/common/GlobalSearch';
import AccessibilityControls from './components/common/AccessibilityControls';
import Loading from './components/ui/Loading';
import FloatingWidgets from './components/ui/FloatingWidgets';
// import ContactModal from './components/ui/ContactModal';

const HomeSection = lazy(() => import('./components/sections/HomeSection'));
const ServicesSection = lazy(() => import('./components/sections/ServicesSection'));
const LeadershipSection = lazy(() => import('./components/sections/LeadershipSection'));
const PublicInfoSection = lazy(() => import('./components/sections/PublicInfoSection'));
const DepartmentsSection = lazy(() => import('./components/sections/DepartmentsSection'));
const StaffSection = lazy(() => import('./components/sections/StaffSection'));
const EmployeesSection = lazy(() => import('./components/sections/EmployeesSection'));
const NewsSection = lazy(() => import('./components/sections/NewsSection'));
const LocationSection = lazy(() => import('./components/sections/LocationSection'));

const GrievanceSection = lazy(() => import('./components/sections/GrievanceSection'));
const SitemapSection = lazy(() => import('./components/sections/SitemapSection'));
const AboutSection = lazy(() => import('./components/sections/AboutSection'));
const HelpSection = lazy(() => import('./components/sections/HelpSection'));
const FeedbackSection = lazy(() => import('./components/sections/FeedbackSection'));
const PolicySection = lazy(() => import('./components/sections/PolicySection'));
const AccessibilitySection = lazy(() => import('./components/sections/AccessibilitySection'));
const SchemesSection = lazy(() => import('./components/sections/SchemesSection'));
const TendersSection = lazy(() => import('./components/sections/TendersSection'));
const PerformanceSection = lazy(() => import('./components/sections/PerformanceSection'));
const DocumentsSection = lazy(() => import('./components/sections/DocumentsSection'));
const SOPSection = lazy(() => import('./components/sections/SOPSection'));
const GallerySection = lazy(() => import('./components/sections/GallerySection'));
const VacanciesSection = lazy(() => import('./components/sections/VacanciesSection'));
const WardsSection = lazy(() => import('./components/sections/WardsSection'));
const NotFound = lazy(() => import('./components/layout/NotFound'));
const GrievanceFilter = lazy(() => import('./components/GrievanceFilter'));
import PDFViewer from './components/ui/PDFViewer';

import { useScroll, useMobileMenu } from './hooks';

// Event helper for non-prop drilling usage
// export const openContactModal = () => {
//   window.dispatchEvent(new Event('open-contact-modal'));
// };

// Main App Component
const App: React.FC = () => {
  const [showSearch, setShowSearch] = useState(false);
  // const [showContactModal, setShowContactModal] = useState(false);
  const [isHeaderVisible, setHeaderVisible] = useState(true);
  const [pdfUrl, setPdfUrl] = useState<string | null>(null);
  const { isOpen: mobileMenuOpen, open: openMobileMenu, close: closeMobileMenu } = useMobileMenu();
  const scrollY = useScroll();

  useEffect(() => {
    setHeaderVisible(scrollY < 50);
  }, [scrollY]);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === '/' && !showSearch) {
        e.preventDefault();
        setShowSearch(true);
      }
      if (e.key === 'Escape') {
        if (pdfUrl) setPdfUrl(null);
        // if (showContactModal) setShowContactModal(false);
      }
    };

    // const handleOpenContact = () => setShowContactModal(true);

    window.addEventListener('keydown', handleKeyDown);
    // window.addEventListener('open-contact-modal', handleOpenContact);

    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      // window.removeEventListener('open-contact-modal', handleOpenContact);
    };
  }, [showSearch, pdfUrl]);

  const handlePreviewPdf = useCallback((url: string) => {
    setPdfUrl(url);
  }, []);

  return (
    <div className="min-h-screen font-sans transition-colors duration-300 bg-background text-foreground">
      <a
        href="#main-content"
        className="sr-only focus:not-sr-only focus:absolute focus:top-4 focus:left-4 focus:z-[100] focus:px-4 focus:py-2 focus:bg-primary focus:text-primary-foreground focus:rounded-lg focus:outline-none"
      >
        Skip to main content
      </a>

      <Header
        isHeaderVisible={isHeaderVisible}
        mobileMenuOpen={mobileMenuOpen}
        setMobileMenuOpen={(isOpen) => isOpen ? openMobileMenu() : closeMobileMenu()}
        onSearchClick={() => setShowSearch(true)}
      />

      <main id="main-content" className="relative z-20 w-full px-4 md:px-8 pt-32 pb-8 space-y-12" role="main">
        <Suspense fallback={<Loading />}>
          <Routes>
            <Route path="/" element={<HomeSection />} />

            {/* Municipal Council */}
            <Route path="/about" element={<AboutSection />} />
            <Route path="/team" element={<LeadershipSection />} />
            <Route path="/departments" element={<DepartmentsSection />} />
            <Route path="/wards" element={<WardsSection />} />
            <Route path="/performance" element={<PerformanceSection />} />

            {/* Offerings */}
            <Route path="/services" element={<ServicesSection />} />
            <Route path="/schemes" element={<SchemesSection />} />
            <Route path="/tenders" element={<TendersSection />} />
            <Route path="/vacancies" element={<VacanciesSection />} />
            {/* Pass preselectedCategory to ServicesSection for Initiatives */}
            <Route path="/initiatives" element={<ServicesSection preselectedCategory="Initiatives" />} />

            {/* Documents */}
            <Route path="/documents" element={<DocumentsSection onPreviewPdf={handlePreviewPdf} />} />
            {/* Legacy/Redirect routes for SEO or bookmarks */}
            <Route path="/acts" element={<Navigate to="/documents" replace />} />
            <Route path="/notices" element={<Navigate to="/documents" replace />} />
            <Route path="/reports" element={<Navigate to="/documents" replace />} />

            {/* Media */}
            <Route path="/gallery/photos" element={<GallerySection />} />
            <Route path="/gallery/videos" element={<NewsSection category="Video" title="Video Gallery" onPreviewPdf={handlePreviewPdf} />} />
            <Route path="/brochures" element={<DocumentsSection category="Brochure" title="Brochures" onPreviewPdf={handlePreviewPdf} />} />
            <Route path="/presentations" element={<DocumentsSection category="Presentation" title="Presentations" onPreviewPdf={handlePreviewPdf} />} />

            {/* Connect */}
            <Route path="/help" element={<HelpSection />} />
            <Route path="/grievance" element={<GrievanceSection />} />
            <Route path="/feedback" element={<FeedbackSection />} />
            <Route path="/engagement" element={<FeedbackSection />} />

            {/* Legacy/Other Routes */}
            <Route path="/public-info" element={<PublicInfoSection onPreviewPdf={handlePreviewPdf} />} />
            <Route path="/staff" element={<StaffSection />} />
            <Route path="/employees" element={<EmployeesSection />} />
            <Route path="/news" element={<NewsSection onPreviewPdf={handlePreviewPdf} />} />
            <Route path="/location" element={<LocationSection />} />
            <Route path="/sitemap" element={<SitemapSection />} />

            {/* Policy Routes */}
            <Route path="/terms" element={<PolicySection type="terms" />} />
            <Route path="/privacy" element={<PolicySection type="privacy" />} />
            <Route path="/hyperlink-policy" element={<PolicySection type="hyperlink" />} />
            <Route path="/copyright-policy" element={<PolicySection type="copyright" />} />

            <Route path="/grievance-filter" element={<GrievanceFilter />} />

            <Route path="/accessibility" element={<AccessibilitySection />} />
            <Route path="/sop" element={<SOPSection />} />
            <Route path="/gallery" element={<Navigate to="/gallery/photos" replace />} />
            <Route path="*" element={<NotFound />} />
          </Routes>

        </Suspense>
      </main>

      <Footer />

      <BackToTopButton />
      <AccessibilityControls />
      <FloatingWidgets />

      {showSearch && <GlobalSearch onClose={() => setShowSearch(false)} />}
      {/* <ContactModal isOpen={showContactModal} onClose={() => setShowContactModal(false)} /> */}

      {pdfUrl && <PDFViewer pdf_url={pdfUrl} onClose={() => setPdfUrl(null)} />}
    </div>
  );
};

export default App;
